import React, { useState, useEffect } from 'react';
import { FiEye, FiMessageSquare, FiEdit, FiTrendingUp, FiTrendingDown } from 'react-icons/fi';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './ClientNutritionStatus.css';

const ClientNutritionStatus = ({ clients, coachId, onViewPlan, onViewLogs, onMessageClient }) => {
  const [clientsWithNutrition, setClientsWithNutrition] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name'); // 'name', 'compliance', 'lastLog'

  useEffect(() => {
    fetchClientNutritionData();
  }, [clients, coachId]);

  const fetchClientNutritionData = async () => {
    if (!clients || clients.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const nutritionData = await Promise.all(
        clients.map(async (client) => {
          try {
            // Fetch active nutrition plan
            const plansRef = collection(db, 'nutritionPlans');
            const planQuery = query(
              plansRef,
              where('clientId', '==', client.id),
              where('coachId', '==', coachId),
              where('status', '==', 'active'),
              limit(1)
            );
            const planSnapshot = await getDocs(planQuery);
            const plan = planSnapshot.empty ? null : { id: planSnapshot.docs[0].id, ...planSnapshot.docs[0].data() };

            // Fetch meal logs for today
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);

            const logsRef = collection(db, 'mealLogs');
            const todayLogsQuery = query(
              logsRef,
              where('userId', '==', client.id),
              where('timestamp', '>=', today),
              where('timestamp', '<', tomorrow)
            );
            const todayLogsSnapshot = await getDocs(todayLogsQuery);
            const todayCompliance = (todayLogsSnapshot.size / 3) * 100; // Assuming 3 meals per day

            // Fetch meal logs for the past week
            const oneWeekAgo = new Date();
            oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

            const weekLogsQuery = query(
              logsRef,
              where('userId', '==', client.id),
              where('timestamp', '>=', oneWeekAgo)
            );
            const weekLogsSnapshot = await getDocs(weekLogsQuery);
            const weekCompliance = (weekLogsSnapshot.size / 21) * 100; // 3 meals × 7 days

            // Get last logged meal
            const lastLogQuery = query(
              logsRef,
              where('userId', '==', client.id),
              orderBy('timestamp', 'desc'),
              limit(1)
            );
            const lastLogSnapshot = await getDocs(lastLogQuery);
            const lastLog = lastLogSnapshot.empty ? null : lastLogSnapshot.docs[0].data();

            return {
              ...client,
              plan: plan,
              todayCompliance: Math.round(todayCompliance),
              weekCompliance: Math.round(weekCompliance),
              lastLogTime: lastLog ? lastLog.timestamp.toDate() : null,
              lastLogMeal: lastLog ? lastLog.mealType : null
            };
          } catch (error) {
            console.error(`Error fetching nutrition data for client ${client.id}:`, error);
            return {
              ...client,
              plan: null,
              todayCompliance: 0,
              weekCompliance: 0,
              lastLogTime: null,
              lastLogMeal: null
            };
          }
        })
      );

      setClientsWithNutrition(nutritionData);
    } catch (error) {
      console.error('Error fetching client nutrition data:', error);
    } finally {
      setLoading(false);
    }
  };

  const sortClients = (clientsData) => {
    const sorted = [...clientsData];
    switch (sortBy) {
      case 'compliance':
        return sorted.sort((a, b) => b.weekCompliance - a.weekCompliance);
      case 'lastLog':
        return sorted.sort((a, b) => {
          if (!a.lastLogTime) return 1;
          if (!b.lastLogTime) return -1;
          return b.lastLogTime - a.lastLogTime;
        });
      case 'name':
      default:
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
  };

  const formatLastLog = (time) => {
    if (!time) return 'Never';

    const now = new Date();
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return time.toLocaleDateString();
  };

  const getComplianceTrend = (todayCompliance, weekCompliance) => {
    if (todayCompliance > weekCompliance) {
      return { icon: FiTrendingUp, color: '#22c55e', label: 'improving' };
    } else if (todayCompliance < weekCompliance) {
      return { icon: FiTrendingDown, color: '#ef4444', label: 'declining' };
    }
    return { icon: null, color: '#6b7280', label: 'stable' };
  };

  if (loading) {
    return (
      <div className="client-nutrition-status loading">
        <h3>Client Nutrition Status</h3>
        <div className="loading-message">Loading client data...</div>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="client-nutrition-status empty">
        <h3>Client Nutrition Status</h3>
        <p>No clients yet. Invite clients to get started.</p>
      </div>
    );
  }

  const sortedClients = sortClients(clientsWithNutrition);

  return (
    <div className="client-nutrition-status">
      <div className="status-header">
        <h3>Client Nutrition Status</h3>
        <div className="sort-controls">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="name">Name</option>
            <option value="compliance">Compliance</option>
            <option value="lastLog">Last Log</option>
          </select>
        </div>
      </div>

      <div className="status-table-container">
        <table className="status-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Current Plan</th>
              <th>Today</th>
              <th>Weekly Avg</th>
              <th>Last Logged</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedClients.map((client) => {
              const trend = getComplianceTrend(client.todayCompliance, client.weekCompliance);
              const TrendIcon = trend.icon;

              return (
                <tr key={client.id}>
                  <td className="client-cell">
                    <div className="client-info">
                      <div className="client-avatar">{client.avatar || '👤'}</div>
                      <div className="client-details">
                        <span className="client-name">{client.name}</span>
                        <span className="client-email">{client.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="plan-cell">
                    {client.plan ? (
                      <span className="plan-name">{client.plan.goal || 'Active'}</span>
                    ) : (
                      <span className="no-plan">No Plan</span>
                    )}
                  </td>
                  <td className="compliance-cell">
                    <div className="compliance-badge-container">
                      <span className={`compliance-badge ${client.todayCompliance >= 80 ? 'good' : client.todayCompliance >= 50 ? 'medium' : 'low'}`}>
                        {client.todayCompliance}%
                      </span>
                      {TrendIcon && (
                        <TrendIcon style={{ color: trend.color, fontSize: '0.875rem' }} />
                      )}
                    </div>
                  </td>
                  <td className="compliance-cell">
                    <span className={`compliance-badge ${client.weekCompliance >= 80 ? 'good' : client.weekCompliance >= 50 ? 'medium' : 'low'}`}>
                      {client.weekCompliance}%
                    </span>
                  </td>
                  <td className="lastlog-cell">
                    <div className="lastlog-info">
                      <span className="lastlog-time">{formatLastLog(client.lastLogTime)}</span>
                      {client.lastLogMeal && (
                        <span className="lastlog-meal">{client.lastLogMeal}</span>
                      )}
                    </div>
                  </td>
                  <td className="actions-cell">
                    <div className="action-buttons">
                      <button
                        className="action-btn view"
                        onClick={() => onViewPlan && onViewPlan(client)}
                        title="View Plan"
                      >
                        <FiEye />
                      </button>
                      <button
                        className="action-btn edit"
                        onClick={() => onViewLogs && onViewLogs(client)}
                        title="View Logs"
                      >
                        <FiEdit />
                      </button>
                      <button
                        className="action-btn message"
                        onClick={() => onMessageClient && onMessageClient(client)}
                        title="Message"
                      >
                        <FiMessageSquare />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientNutritionStatus;
